/**
 * URL Sources API Service
 * Phase 0: URL Crawler & Web Content Indexing
 */

import { apiClient } from './client';

export interface UrlSource {
  id: string;
  url: string;
  normalizedUrl: string;
  domain: string;
  knowledgeBaseId?: string;
  crawlFrequency: 'manual' | 'daily' | 'weekly' | 'monthly';
  maxDepth?: number;
  respectRobotsTxt?: boolean;
  extractMainContent?: boolean;
  includeImages?: boolean;
  includeLinks?: boolean;
  customSelectors?: {
    content?: string;
    title?: string;
    exclude?: string[];
  };
  status: 'pending' | 'crawling' | 'indexed' | 'failed' | 'disabled';
  pageTitle?: string;
  pageDescription?: string;
  pageKeywords?: string[];
  detectedLanguage?: string;
  lastCrawlAt?: string;
  nextCrawlAt?: string;
  lastContentHash?: string;
  lastError?: string;
  errorCount: number;
  crawlCount: number;
  successfulCrawls: number;
  avgCrawlTimeMs?: number;
  lastHttpStatus?: number;
  contentLengthBytes?: number;
  documentId?: string;
  // Markdown storage on S3
  markdownS3Key?: string;
  markdownS3Url?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CrawlJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  httpStatus?: number;
  crawlTimeMs?: number;
  contentChanged?: boolean;
  errorMessage?: string;
  chunksCreated?: number;
}

export interface ListUrlsResponse {
  success: boolean;
  urls: UrlSource[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetUrlResponse {
  success: boolean;
  url: UrlSource;
  recentJobs: CrawlJob[];
}

export interface AddUrlRequest {
  url: string;
  knowledgeBaseId?: string;
  crawlFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly';
  maxDepth?: number;
  respectRobotsTxt?: boolean;
  extractMainContent?: boolean;
  includeImages?: boolean;
  includeLinks?: boolean;
  customSelectors?: {
    content?: string;
    title?: string;
    exclude?: string[];
  };
}

export interface AddUrlResponse {
  success: boolean;
  message: string;
  url: {
    id: string;
    url: string;
    normalizedUrl: string;
    domain: string;
    status: string;
    crawlFrequency: string;
    nextCrawlAt?: string;
    createdAt: string;
  };
}

export interface BulkAddUrlsRequest {
  urls: string[];
  knowledgeBaseId?: string;
  crawlFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly';
}

export interface BulkAddUrlsResponse {
  success: boolean;
  message: string;
  total: number;
  successful: number;
  failed: number;
  results: Array<{
    url: string;
    success: boolean;
    id?: string;
    error?: string;
  }>;
}

export interface ImportSitemapRequest {
  sitemapUrl: string;
  knowledgeBaseId?: string;
  crawlFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly';
  maxUrls?: number;
  includePattern?: string;
  excludePattern?: string;
}

export interface ImportSitemapResponse {
  success: boolean;
  message: string;
  sitemapUrl: string;
  totalFound: number;
  added: number;
  duplicates: number;
  errors: number;
}

export interface UpdateUrlRequest {
  crawlFrequency?: 'manual' | 'daily' | 'weekly' | 'monthly';
  maxDepth?: number;
  respectRobotsTxt?: boolean;
  extractMainContent?: boolean;
  includeImages?: boolean;
  includeLinks?: boolean;
  customSelectors?: {
    content?: string;
    title?: string;
    exclude?: string[];
  };
  status?: 'pending' | 'disabled';
}

export interface RefreshUrlResponse {
  success: boolean;
  message: string;
  jobId: string;
  contentChanged?: boolean;
  crawlTimeMs?: number;
  contentLength?: number;
  title?: string;
  error?: string;
  errorType?: string;
}

export interface CrawlAllPendingResponse {
  success: boolean;
  message: string;
  total: number;
  queued: number;
  errors?: string[];
}

/**
 * List all URL sources
 */
export async function listUrls(params?: {
  knowledgeBaseId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ListUrlsResponse> {
  const response = await apiClient.get('/urls', { params });
  return response.data;
}

/**
 * Get single URL source details
 */
export async function getUrl(id: string): Promise<GetUrlResponse> {
  const response = await apiClient.get(`/urls/${id}`);
  return response.data;
}

/**
 * Add a single URL for indexing
 */
export async function addUrl(data: AddUrlRequest): Promise<AddUrlResponse> {
  const response = await apiClient.post('/urls', data);
  return response.data;
}

/**
 * Add multiple URLs for indexing
 */
export async function addBulkUrls(data: BulkAddUrlsRequest): Promise<BulkAddUrlsResponse> {
  const response = await apiClient.post('/urls/bulk', data);
  return response.data;
}

/**
 * Import URLs from sitemap
 */
export async function importSitemap(data: ImportSitemapRequest): Promise<ImportSitemapResponse> {
  const response = await apiClient.post('/urls/sitemap', data);
  return response.data;
}

/**
 * Update URL source settings
 */
export async function updateUrl(id: string, data: UpdateUrlRequest): Promise<{ success: boolean; message: string; url: UrlSource }> {
  const response = await apiClient.put(`/urls/${id}`, data);
  return response.data;
}

/**
 * Delete a URL source
 */
export async function deleteUrl(id: string): Promise<{ success: boolean; message: string; deletedDocumentId?: string }> {
  const response = await apiClient.delete(`/urls/${id}`);
  return response.data;
}

/**
 * Trigger manual refresh/crawl for a URL
 */
export async function refreshUrl(id: string): Promise<RefreshUrlResponse> {
  const response = await apiClient.post(`/urls/${id}/refresh`);
  return response.data;
}

/**
 * Crawl all pending URLs at once
 */
export async function crawlAllPending(): Promise<CrawlAllPendingResponse> {
  const response = await apiClient.post('/urls/crawl-pending');
  return response.data;
}

/**
 * Crawl specific selected URLs
 */
export async function crawlSelectedUrls(urlIds: string[]): Promise<CrawlAllPendingResponse> {
  const response = await apiClient.post('/urls/crawl-selected', { urlIds });
  return response.data;
}

/**
 * Reset stuck URLs (in "crawling" status) back to "pending"
 */
export async function resetStuckUrls(): Promise<{ success: boolean; message: string; resetCount: number }> {
  const response = await apiClient.post('/urls/reset-stuck');
  return response.data;
}

/**
 * Get a presigned URL to view the markdown content
 */
export async function getMarkdownUrl(id: string): Promise<{ success: boolean; url: string }> {
  const response = await apiClient.get(`/urls/${id}/markdown`);
  return response.data;
}

export const urlsApi = {
  listUrls,
  getUrl,
  addUrl,
  addBulkUrls,
  importSitemap,
  updateUrl,
  deleteUrl,
  refreshUrl,
  crawlAllPending,
  crawlSelectedUrls,
  resetStuckUrls,
  getMarkdownUrl,
};

export default urlsApi;
